import React, { useState, useEffect, useCallback } from "react";
import api from "components/api/api";
import "./LoginReportPage.scss";

const TYPE_OPTIONS = [
  { value: "ALL", label: "All staff" },
  { value: "CSR", label: "CSR" },
  { value: "CALL_CENTER", label: "Call center" },
];

const PERIOD_MODES = [
  { value: "ALL", label: "Any time" },
  { value: "DATE", label: "Specific date" },
  { value: "DAY", label: "Day" },
  { value: "MONTH", label: "Month" },
  { value: "YEAR", label: "Year" },
];

const MONTH_OPTIONS = [
  { value: 1, label: "January" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
];

const DAY_OPTIONS = Array.from({ length: 31 }, (_, i) => i + 1);
const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 8 }, (_, i) => CURRENT_YEAR - i);
const PAGE_SIZE = 20;

function FilterBar({
  search,
  onSearchChange,
  type,
  onTypeChange,
  periodMode,
  onPeriodModeChange,
  day,
  onDayChange,
  month,
  onMonthChange,
  year,
  onYearChange,
  date,
  onDateChange,
}) {
  return (
    <div className="filter-card">
      <div className="filter-card__search">
        <svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle
            cx="9"
            cy="9"
            r="6.5"
            stroke="currentColor"
            strokeWidth="1.6"
          />
          <path
            d="M14 14L18 18"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
          />
        </svg>
        <input
          type="text"
          placeholder="Search by ID or name"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
        />
      </div>

      <select
        className="filter-card__select"
        value={type}
        onChange={(e) => onTypeChange(e.target.value)}
      >
        {TYPE_OPTIONS.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>

      <select
        className="filter-card__select"
        value={periodMode}
        onChange={(e) => onPeriodModeChange(e.target.value)}
      >
        {PERIOD_MODES.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>

      {periodMode === "DATE" && (
        <input
          type="date"
          className="filter-card__select filter-card__select--date"
          value={date || ""}
          onChange={(e) => onDateChange(e.target.value)}
        />
      )}
      {periodMode === "DAY" && (
        <select
          className="filter-card__select"
          value={day || ""}
          onChange={(e) => onDayChange(e.target.value)}
        >
          <option value="">Day</option>
          {DAY_OPTIONS.map((d) => (
            <option key={d} value={d}>
              {d}
            </option>
          ))}
        </select>
      )}
      {periodMode === "MONTH" && (
        <select
          className="filter-card__select"
          value={month || ""}
          onChange={(e) => onMonthChange(e.target.value)}
        >
          <option value="">Month</option>
          {MONTH_OPTIONS.map((m) => (
            <option key={m.value} value={m.value}>
              {m.label}
            </option>
          ))}
        </select>
      )}
      {periodMode === "YEAR" && (
        <select
          className="filter-card__select"
          value={year || ""}
          onChange={(e) => onYearChange(e.target.value)}
        >
          <option value="">Year</option>
          {YEAR_OPTIONS.map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </select>
      )}
    </div>
  );
}

function PaginationBar({ page, totalPages, totalElements, onPrev, onNext }) {
  return (
    <div className="pager">
      <span className="pager__count">
        {totalElements === 0
          ? "No records"
          : `${totalElements} record${totalElements === 1 ? "" : "s"}`}
        {totalPages > 0 && ` · page ${page + 1} of ${totalPages}`}
      </span>
      <div className="pager__buttons">
        <button
          type="button"
          className="btn-ghost"
          disabled={page === 0}
          onClick={onPrev}
        >
          Previous
        </button>
        <button
          type="button"
          className="btn-primary"
          disabled={page + 1 >= totalPages}
          onClick={onNext}
        >
          Next
        </button>
      </div>
    </div>
  );
}

function LoginReportPage() {
  const [activeTab, setActiveTab] = useState("lastLogin");

  // ---- Tab 1: Last Login ----
  const [search1, setSearch1] = useState("");
  const [searchInput1, setSearchInput1] = useState("");
  const [type1, setType1] = useState("ALL");
  const [periodMode1, setPeriodMode1] = useState("ALL");
  const [day1, setDay1] = useState(null);
  const [month1, setMonth1] = useState(null);
  const [year1, setYear1] = useState(null);
  const [date1, setDate1] = useState(null);
  const [page1, setPage1] = useState(0);
  const [rows1, setRows1] = useState([]);
  const [totalPages1, setTotalPages1] = useState(0);
  const [totalElements1, setTotalElements1] = useState(0);
  const [loading1, setLoading1] = useState(false);
  const [error1, setError1] = useState(null);

  const fetchLastLogins = useCallback(async () => {
    setLoading1(true);
    setError1(null);
    try {
      const response = await api.fetchStaffLastLogins({
        search: search1,
        type: type1,
        day: periodMode1 === "DAY" ? day1 : null,
        month: periodMode1 === "MONTH" ? month1 : null,
        year: periodMode1 === "YEAR" ? year1 : null,
        date: periodMode1 === "DATE" ? date1 : null,
        page: page1,
        size: PAGE_SIZE,
      });
      const data = response?.data?.data;
      setRows1(data?.content || []);
      setTotalPages1(data?.totalPages || 0);
      setTotalElements1(data?.totalElements || 0);
    } catch {
      setError1("Couldn't load the login report. Try again.");
      setRows1([]);
    } finally {
      setLoading1(false);
    }
  }, [search1, type1, periodMode1, day1, month1, year1, date1, page1]);

  useEffect(() => {
    if (activeTab === "lastLogin") fetchLastLogins();
  }, [activeTab, fetchLastLogins]);
  useEffect(() => {
    const t = setTimeout(() => {
      setPage1(0);
      setSearch1(searchInput1);
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput1]);
  useEffect(() => {
    setPage1(0);
  }, [type1, periodMode1, day1, month1, year1, date1]);

  // ---- Tab 2: Login Count ----
  const [search2, setSearch2] = useState("");
  const [searchInput2, setSearchInput2] = useState("");
  const [type2, setType2] = useState("ALL");
  const [periodMode2, setPeriodMode2] = useState("ALL");
  const [day2, setDay2] = useState(null);
  const [month2, setMonth2] = useState(null);
  const [year2, setYear2] = useState(null);
  const [date2, setDate2] = useState(null);
  const [page2, setPage2] = useState(0);
  const [rows2, setRows2] = useState([]);
  const [totalPages2, setTotalPages2] = useState(0);
  const [totalElements2, setTotalElements2] = useState(0);
  const [loading2, setLoading2] = useState(false);
  const [error2, setError2] = useState(null);

  const fetchLoginCounts = useCallback(async () => {
    setLoading2(true);
    setError2(null);
    try {
      const response = await api.fetchStaffLoginCounts({
        search: search2,
        type: type2,
        day: periodMode2 === "DAY" ? day2 : null,
        month: periodMode2 === "MONTH" ? month2 : null,
        year: periodMode2 === "YEAR" ? year2 : null,
        date: periodMode2 === "DATE" ? date2 : null,
        page: page2,
        size: PAGE_SIZE,
      });
      const data = response?.data?.data;
      setRows2(data?.content || []);
      setTotalPages2(data?.totalPages || 0);
      setTotalElements2(data?.totalElements || 0);
    } catch {
      setError2("Couldn't load the login count report. Try again.");
      setRows2([]);
    } finally {
      setLoading2(false);
    }
  }, [search2, type2, periodMode2, day2, month2, year2, date2, page2]);

  useEffect(() => {
    if (activeTab === "loginCount") fetchLoginCounts();
  }, [activeTab, fetchLoginCounts]);
  useEffect(() => {
    const t = setTimeout(() => {
      setPage2(0);
      setSearch2(searchInput2);
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput2]);
  useEffect(() => {
    setPage2(0);
  }, [type2, periodMode2, day2, month2, year2, date2]);

  return (
    <div className="login-report">
      <div className="login-report__header">
        <h2>Staff Login Report</h2>
        <div className="tabbar">
          <span
            className="tabbar__slider"
            style={{
              transform: `translateX(${activeTab === "lastLogin" ? 0 : 100}%)`,
            }}
          />
          <button
            type="button"
            className={activeTab === "lastLogin" ? "is-active" : ""}
            onClick={() => setActiveTab("lastLogin")}
          >
            Last Login
          </button>
          <button
            type="button"
            className={activeTab === "loginCount" ? "is-active" : ""}
            onClick={() => setActiveTab("loginCount")}
          >
            Login Count
          </button>
        </div>
      </div>

      {activeTab === "lastLogin" && (
        <>
          <FilterBar
            search={searchInput1}
            onSearchChange={setSearchInput1}
            type={type1}
            onTypeChange={setType1}
            periodMode={periodMode1}
            onPeriodModeChange={setPeriodMode1}
            day={day1}
            onDayChange={setDay1}
            month={month1}
            onMonthChange={setMonth1}
            year={year1}
            onYearChange={setYear1}
            date={date1}
            onDateChange={setDate1}
          />

          {error1 && <div className="alert">{error1}</div>}

          <div className="table-card">
            <div className="table-card__scroll">
              <table>
                <thead>
                  <tr>
                    <th>Staff ID</th>
                    <th>Name</th>
                    <th>Date</th>
                    <th>Time</th>
                  </tr>
                </thead>
                <tbody>
                  {loading1 ? (
                    <tr>
                      <td colSpan={4} className="state">
                        Loading…
                      </td>
                    </tr>
                  ) : rows1.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="state">
                        No logins match these filters
                      </td>
                    </tr>
                  ) : (
                    rows1.map((row) => (
                      <tr key={row.staffId}>
                        <td className="mono">{row.staffId}</td>
                        <td>{row.name}</td>
                        <td>{row.date}</td>
                        <td className="mono">{row.time}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <PaginationBar
              page={page1}
              totalPages={totalPages1}
              totalElements={totalElements1}
              onPrev={() => setPage1((p) => Math.max(0, p - 1))}
              onNext={() => setPage1((p) => p + 1)}
            />
          </div>
        </>
      )}

      {activeTab === "loginCount" && (
        <>
          <FilterBar
            search={searchInput2}
            onSearchChange={setSearchInput2}
            type={type2}
            onTypeChange={setType2}
            periodMode={periodMode2}
            onPeriodModeChange={setPeriodMode2}
            day={day2}
            onDayChange={setDay2}
            month={month2}
            onMonthChange={setMonth2}
            year={year2}
            onYearChange={setYear2}
            date={date2}
            onDateChange={setDate2}
          />

          {error2 && <div className="alert">{error2}</div>}

          <div className="table-card">
            <div className="table-card__scroll">
              <table>
                <thead>
                  <tr>
                    <th>Staff ID</th>
                    <th>Username</th>
                    <th>Login Count</th>
                  </tr>
                </thead>
                <tbody>
                  {loading2 ? (
                    <tr>
                      <td colSpan={3} className="state">
                        Loading…
                      </td>
                    </tr>
                  ) : rows2.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="state">
                        No logins match these filters
                      </td>
                    </tr>
                  ) : (
                    rows2.map((row) => (
                      <tr key={row.staffId}>
                        <td className="mono">{row.staffId}</td>
                        <td>{row.username}</td>
                        <td className="mono">{row.loginCount}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <PaginationBar
              page={page2}
              totalPages={totalPages2}
              totalElements={totalElements2}
              onPrev={() => setPage2((p) => Math.max(0, p - 1))}
              onNext={() => setPage2((p) => p + 1)}
            />
          </div>
        </>
      )}
    </div>
  );
}

export default LoginReportPage;
